import { useEffect, useState } from "react";
import type { JSX } from "react";
import type { Recipe, RecipeIngredient } from "../../shared/recipes";
import { formatIngredientDisplay } from "../../shared/quantity";
import { formatTagPath, normalizeRecipeTags } from "../../shared/recipe-tags";
import { fetchShoppingLists, createShoppingList, addShoppingItem } from "../api/shopping-list";
import { deleteRecipe, fetchRecipe, startCookSession } from "../api/recipes";
import { formatIngredientName } from "./format-ingredient";
import { StepList } from "./StepList";

type RecipeDetailProps = {
  recipeId: string;
  onNavigate: (path: string) => void;
};

function formatDetailIngredient(ingredient: RecipeIngredient): string {
  return ingredient.quantity === undefined
    ? formatIngredientName(ingredient.name)
    : formatIngredientDisplay(ingredient);
}

export function RecipeDetail({ recipeId, onNavigate }: RecipeDetailProps): JSX.Element {
  const [recipe, setRecipe] = useState<Recipe | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [starting, setStarting] = useState(false);
  const [addingToShopping, setAddingToShopping] = useState(false);

  useEffect(() => {
    let active = true;
    void fetchRecipe(recipeId)
      .then((loaded) => {
        if (active) {
          setRecipe(loaded);
        }
      })
      .catch(() => {
        if (active) {
          setError("Recipe not found.");
        }
      });
    return () => {
      active = false;
    };
  }, [recipeId]);

  async function onStartCooking(): Promise<void> {
    setStarting(true);
    try {
      const { session } = await startCookSession(recipeId);
      onNavigate(`/recipes/${recipeId}/cook/${session.id}`);
    } catch {
      setError("Could not start cooking session.");
      setStarting(false);
    }
  }


  async function onAddToShoppingList(): Promise<void> {
    if (recipe === undefined) {
      return;
    }
    setAddingToShopping(true);
    try {
      const lists = await fetchShoppingLists();
      const list = lists[0] ?? await createShoppingList("Weekly shop");
      for (const ingredient of recipe.ingredients) {
        await addShoppingItem(list.id, {
          displayName: ingredient.name,
          quantityText: ingredient.rawText,
          sourceRefs: [{
            type: "recipe",
            recipeId: recipe.id,
            recipeTitle: recipe.title,
            ingredientId: ingredient.id,
            ingredientName: ingredient.name,
            quantityText: ingredient.rawText
          }]
        });
      }
      onNavigate(`/shopping-lists/${list.id}`);
    } catch {
      setError("Could not add ingredients to shopping list.");
      setAddingToShopping(false);
    }
  }

  async function onDelete(): Promise<void> {
    if (!window.confirm("Delete this recipe?")) {
      return;
    }
    try {
      await deleteRecipe(recipeId);
      onNavigate("/recipes");
    } catch {
      setError("Could not delete recipe.");
    }
  }

  if (error !== undefined) {
    return (
      <div className="recipes-panel">
        <p className="error-text" role="alert">{error}</p>
        <button className="secondary-action" type="button" onClick={() => { onNavigate("/recipes"); }}>
          Back to library
        </button>
      </div>
    );
  }

  if (recipe === undefined) {
    return <div className="recipes-panel"><p className="muted-text">Loading recipe…</p></div>;
  }

  const tags = normalizeRecipeTags(recipe.tags);
  const hasEstimatedTime = recipe.estimatedMinutes !== undefined;

  return (
    <div className="recipes-panel recipe-detail-panel">
      <header className="recipe-detail-hero">
        <div className="recipe-detail-hero-content">
          <p className="eyebrow">Recipe</p>
          <h2>{recipe.title}</h2>
          <div className="recipe-detail-meta" aria-label="Recipe details">
            <span>Serves {recipe.servings}</span>
            {hasEstimatedTime ? <span>~{String(recipe.estimatedMinutes)} min</span> : null}
          </div>
          {recipe.source !== undefined ? <p className="recipe-source">Source: {recipe.source}</p> : null}
          {tags.length > 0 ? (
            <div className="recipe-tag-list recipe-detail-tags">
              {tags.map((tag) => (
                <span key={formatTagPath(tag.path)} className="recipe-tag-chip readonly">
                  {formatTagPath(tag.path)}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div className="detail-actions recipe-detail-actions" aria-label="Recipe actions">
          <button className="primary-action" type="button" disabled={starting} onClick={() => { void onStartCooking(); }}>
            {starting ? "Starting…" : "Start cooking"}
          </button>
          <button className="secondary-action" type="button" disabled={addingToShopping} onClick={() => { void onAddToShoppingList(); }}>
            {addingToShopping ? "Adding…" : "Add to shopping"}
          </button>
          <button className="secondary-action" type="button" onClick={() => { onNavigate(`/recipes/${recipeId}/edit`); }}>
            Edit
          </button>
          <button className="secondary-action danger" type="button" onClick={() => { void onDelete(); }}>
            Delete
          </button>
        </div>
      </header>

      <div className="recipe-detail-layout">
        <aside className="recipe-detail-sidebar" aria-label="Recipe preparation details">
          {recipe.ingredients.length > 0 ? (
            <section className="detail-section recipe-detail-card">
              <div className="detail-section-heading">
                <h3>Ingredients</h3>
                <span>{recipe.ingredients.length}</span>
              </div>
              <ul className="recipe-detail-list">
                {recipe.ingredients.map((ingredient) => (
                  <li key={ingredient.id}>{formatDetailIngredient(ingredient)}</li>
                ))}
              </ul>
            </section>
          ) : null}

          {recipe.equipment.length > 0 ? (
            <section className="detail-section recipe-detail-card recipe-equipment-card">
              <div className="detail-section-heading">
                <h3>Equipment</h3>
                <span>{recipe.equipment.length}</span>
              </div>
              <ul className="recipe-detail-list compact">
                {recipe.equipment.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </section>
          ) : null}
        </aside>

        <section className="detail-section recipe-steps-section">
          <StepList steps={recipe.steps} ingredients={recipe.ingredients} defaultAllExpanded />
        </section>
      </div>

      <button className="text-button back-link" type="button" onClick={() => { onNavigate("/recipes"); }}>
        ← Back to library
      </button>
    </div>
  );
}
